/**
 * RulesTabContent Component
 *
 * Rules management content for the Audit Center rules tab
 * Reuses the same components and logic from RulesManagementPage
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Drawer, Grid, useMediaQuery, useTheme } from '@mui/material';

import { DeleteRuleDialog, RuleDetailPanel, RuleFormPanel } from '@features/rules/components';
import { RulesList } from '@features/rules/components/RulesList';
import {
  useDeleteRuleMutation,
  useGetRuleQuery,
  useGetRulesQuery,
  useSearchRulesQuery,
} from '@features/rules/store/api';
import type {
  ComplianceRuleDocument,
  LearningRuleDocument,
  ProviderRuleResponse,
} from '@features/rules/types';

import type { RulesFilters } from '../filters';

import { AddRuleCard } from './AddRuleCard';
import { RulesSummaryCard } from './RulesSummaryCard';

interface RulesTabContentProps {
  /** Search query from parent */
  searchQuery?: string;
  /** Filter settings from parent */
  filters?: RulesFilters;
}

export const RulesTabContent: React.FC<RulesTabContentProps> = ({
  searchQuery: externalSearchQuery = '',
  filters,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Use filters from props or defaults
  const scope = filters?.scope ?? 'both';
  const ruleType = filters?.ruleType ?? 'all';
  const showDisabled = filters?.showDisabled ?? false;

  // State for pagination
  const [page, setPage] = useState(1);

  // Selection state for detail drawer - store ID and fetch via query for auto-refresh on invalidation
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);

  // Panel mode: 'view' | 'create' | 'edit'
  const [panelMode, setPanelMode] = useState<'view' | 'create' | 'edit'>('view');

  // Fetch selected rule data via query - auto-refreshes when Rule tag is invalidated
  const { data: selectedRule } = useGetRuleQuery(selectedRuleId ?? '', { skip: !selectedRuleId });

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<ProviderRuleResponse | null>(null);

  // Delete mutation
  const [deleteRule, { isLoading: isDeleting }] = useDeleteRuleMutation();

  // Debounce search query from props
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(externalSearchQuery);
      // Reset to page 1 on search change
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [externalSearchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [scope, ruleType, showDisabled]);

  // Determine if we are searching or listing
  const isSearching = debouncedSearchQuery.length > 0;

  // Standard List Query
  const {
    data: listData,
    isLoading: isListLoading,
    isFetching: isListFetching,
  } = useGetRulesQuery(
    {
      page,
      per_page: 20,
      scope,
      rule_type: ruleType === 'all' ? undefined : ruleType,
      include_disabled: showDisabled,
    },
    { skip: isSearching }, // Skip list query when searching
  );

  // Search Query (GET - automatically refetches when params change or cache is invalidated)
  const {
    data: searchData,
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
  } = useSearchRulesQuery(
    {
      query: debouncedSearchQuery,
      rule_type: ruleType === 'all' ? undefined : ruleType,
      top_k: 20,
      include_disabled: showDisabled,
    },
    { skip: !isSearching }, // Skip when not searching
  );

  // Handlers
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleAddRule = useCallback(() => {
    setSelectedRuleId(null);
    setPanelMode('create');
  }, []);

  const handleRuleClick = useCallback((rule: ProviderRuleResponse) => {
    setSelectedRuleId(rule.rule_id);
    setPanelMode('view');
  }, []);

  const handlePanelClose = useCallback(() => {
    setSelectedRuleId(null);
    setPanelMode('view');
  }, []);

  const handleEditRule = useCallback(() => {
    if (selectedRuleId) {
      setPanelMode('edit');
    }
  }, [selectedRuleId]);

  const handleDeleteRule = useCallback(() => {
    if (selectedRule) {
      setRuleToDelete(selectedRule);
      setDeleteDialogOpen(true);
    }
  }, [selectedRule]);

  const handleConfirmDelete = useCallback(async () => {
    if (!ruleToDelete) return;

    try {
      await deleteRule({
        ruleId: ruleToDelete.rule_id,
        ruleType: ruleToDelete.rule_type,
      }).unwrap();
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
      setSelectedRuleId(null);
      setPanelMode('view');
    } catch (error) {
      console.error('Failed to delete rule:', error);
    }
  }, [ruleToDelete, deleteRule]);

  const handleDeleteDialogClose = useCallback(() => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    }
  }, [isDeleting]);

  const handleFormSuccess = useCallback(() => {
    if (panelMode === 'create') {
      setSelectedRuleId(null);
      setPanelMode('view');
    } else if (panelMode === 'edit' && selectedRuleId) {
      setPanelMode('view');
    }
  }, [panelMode, selectedRuleId]);

  // Derived state for display
  const isLoading = isSearching
    ? isSearchLoading || isSearchFetching
    : isListLoading || isListFetching;

  // Build rules list from search or list data
  // Note: r.record is typed as `any` in the generated types, so we disable eslint for safe member access
  /* eslint-disable @typescript-eslint/no-unsafe-member-access */
  const rules = isSearching
    ? (searchData?.results ?? [])
        .filter(
          (r): r is typeof r & { record: NonNullable<typeof r.record> } =>
            r.record != null && r.record.rule != null,
        )
        .map(r => ({
          rule: r.record.rule as ComplianceRuleDocument | LearningRuleDocument,
          rule_id: r.metadata.rule_id,
          rule_type: r.metadata.rule_type,
          is_global: (r.record.is_global as boolean | undefined) ?? false,
          is_disabled: r.metadata.is_disabled ?? false,
          organization_id: r.metadata.organization_id,
        }))
    : (listData?.items ?? []);
  /* eslint-enable @typescript-eslint/no-unsafe-member-access */

  const totalPages = isSearching ? 1 : listData?.total_pages || 1;

  return (
    <Box sx={{ pb: 4 }}>
      {/* Stats Cards Row */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <AddRuleCard onAddManual={handleAddRule} onUpload={() => {}} />
        </Grid>
        <Grid item xs={12} md={1.8}>
          <RulesSummaryCard label="At Risk Rules" value={0} />
        </Grid>
        <Grid item xs={12} md={1.8}>
          <RulesSummaryCard label="Clinicians Affected" value={0} />
        </Grid>
        <Grid item xs={12} md={1.8}>
          <RulesSummaryCard label="Revenue at risk" value="$0" />
        </Grid>
        <Grid item xs={12} md={1.8}>
          <RulesSummaryCard label="Blocked Rules" value={0} />
        </Grid>
        <Grid item xs={12} md={1.8}>
          <RulesSummaryCard label="Modified Rules" value={0} />
        </Grid>
      </Grid>

      {/* Rules List (Table layout) */}
      <RulesList
        rules={rules}
        isLoading={isLoading}
        page={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onRuleClick={handleRuleClick}
      />

      {/* Detail/Form Drawer */}
      <Drawer
        anchor="right"
        open={!!selectedRuleId || panelMode === 'create' || panelMode === 'edit'}
        onClose={handlePanelClose}
        PaperProps={{
          sx: { width: isMobile ? '100%' : 600, p: 0 },
        }}
      >
        <Box sx={{ height: '100%', overflow: 'hidden' }}>
          {panelMode === 'view' && selectedRuleId ? (
            selectedRule ? (
              <RuleDetailPanel
                rule={selectedRule}
                onClose={handlePanelClose}
                onEdit={handleEditRule}
                onDelete={handleDeleteRule}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <CircularProgress />
              </Box>
            )
          ) : panelMode === 'create' || panelMode === 'edit' ? (
            <RuleFormPanel
              mode={panelMode}
              rule={selectedRule ?? null}
              onClose={handlePanelClose}
              onSuccess={handleFormSuccess}
            />
          ) : null}
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <DeleteRuleDialog
        open={deleteDialogOpen}
        rule={ruleToDelete}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </Box>
  );
};
